'use client';

import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import { useTheme } from '@unity/cloud-ui/styles';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';
import { customPalette } from '@/components/theme/custom-palette';

const phosphorWeights = ['thin', 'light', 'regular', 'bold', 'fill', 'duotone'] as const;

const getPhosphorIcon = (iconName) => {
  const availableIcons = Object.keys(PhosphorIcons);
  if (!iconName || !availableIcons.includes(iconName)) {
    return null;
  }

  return PhosphorIcons[iconName];
};

const getPhosphorWeight = (weight) => {
  const isValidWeight = phosphorWeights.includes(weight);
  return isValidWeight ? weight : 'regular';
};

export const Icon = (props: z.input<typeof iconSchema>) => {
  const { name, color, weight = 'regular', size = 16 } = props;
  const { palette } = useTheme();

  const PhosphorIcon = getPhosphorIcon(name);
  const phosphorWeight = getPhosphorWeight(weight);
  const phosphorColor = color && color in palette.icons ? palette.icons[color] : palette.primary.main;

  if (!PhosphorIcon) {
    return null;
  }

  return <PhosphorIcon color={phosphorColor} alt={name} size={size} weight={phosphorWeight} />;
};

const documentationURL = getComponentDocumentationUrl('icons');

const descriptions = {
  name: 'The name of the icon from [Phosphor icons](https://phosphoricons.com/).',
  color: 'The color of the icon.',
  weight: 'The weight of the icon.',
  size: 'The size of the icon.',
  schema: `Display an icon from [Phosphor icons](https://phosphoricons.com/).\n\n\n**Example**\n\`\`\`mdx\n<Icon\n  name="airplane"\n  color="blue"\n  weight="bold"\n  size={24}\n/>\n\`\`\`\n\n${documentationURL}`,
};

export const iconSchema = z
  .object({
    name: z.string().describe(descriptions.name),
    color: z
      .enum(Object.keys(customPalette.light.icons) as [string, ...string[]])
      .describe(descriptions.color)
      .optional(),
    weight: z.enum(phosphorWeights).describe(descriptions.weight).default('regular'),
    size: z.number().describe(descriptions.size).optional().default(16),
  })
  .describe(descriptions.schema);
